import React, { memo } from "react";
import cn from "classnames";

import { isMobile } from "../../../../../../common/parameters";
import { isHintable } from "../utils";

import {
    StyledBar,
    Note,
    Tone,
    ToneIn,
    Modifier,
    Barline,
    DoubleBarline,
    FinalBarline,
    FinalStartBarline,
    FinalBothBarline,
    DotsBefore,
} from "./styles";
import { IProps, IBarAccidental } from "./interfaces";
import { convertToNewColors } from "../../../../../utils/convertToNewColors";

export const clefOffset: { [prop: string]: number } = {
    treble: 0,
    "treble-octave": 7,
    bass: 12,
};

const Bar = ({
    bar,
    fullToneset,
    completed,
    size,
    isLow,
    lowDim,
    solutions,
    scale,
    incorrects,
    clef,
    barIndex,
    submited,
    data,
    accidental,
    bars,
    barlines,
    checkPlaying,
    clickTone,
    playingProps,
    playing,
    isToneset,
    isChristmas,
}: IProps) => {
    const barAccidentals: IBarAccidental[] = [];
    const barlineType =
        barlines?.[barlines.findIndex(barline => barline.bar === barIndex && !barline.trunc)]?.type || "";

    const truncBarline = barlines.find(barline => barline.bar === barIndex && barline.trunc);
    return (
        <StyledBar>
            {bar.map((note, noteIndex) => {
                const hintable = isHintable({ bars, bar, noteIndex, hint: data.hint, barIndex });
                let tiedOff = false;
                let isLastNoteOfBar = !bar[noteIndex + 1];
                if (
                    noteIndex === 0 &&
                    bars[barIndex - 1] &&
                    bars[barIndex - 1].length > 0 &&
                    bars[barIndex - 1][bars[barIndex - 1].length - 1].tie
                ) {
                    tiedOff = true;
                }
                if (noteIndex > 0 && bars[barIndex][noteIndex - 1].tie) {
                    tiedOff = true;
                }
                return (
                    <Note
                        key={noteIndex}
                        style={{
                            width: (4 / note.l) * size.x * (note.d ? 1.5 : 1) * (note.t ? 0.67 : 1) * lowDim,
                        }}
                    >
                        {fullToneset.map((fullTone, index) => {
                            let ledger = false;
                            let solutionNote = solutions?.[barIndex]?.[noteIndex] || null;
                            if (solutionNote && Object.keys(solutionNote).length === 0) solutionNote = null;
                            if (
                                (-fullTone > 5 + clefOffset[clef] || -fullTone < -5 + clefOffset[clef]) &&
                                -fullTone % 2 === 0
                            ) {
                                if (
                                    !completed ||
                                    (solutionNote && fullTone < -5 && solutionNote.p! <= fullTone) ||
                                    (solutionNote && fullTone > 5 && solutionNote.p! >= fullTone)
                                ) {
                                    ledger = true;
                                }
                            }
                            const sf = scale[scale.findIndex(tones => tones.tone === fullTone)]?.s || 0;
                            const prevBarAccidentals = [...barAccidentals];
                            if (
                                solutionNote &&
                                solutionNote.p === fullTone &&
                                (
                                    (solutionNote.a === 1 || solutionNote.a === -1) ||
                                    solutionNote.a === 0
                                )
                            ) {
                                let alreadyHasToneIndex = barAccidentals.findIndex(a => a.fullTone === fullTone);
                                if (alreadyHasToneIndex !== -1) {
                                    barAccidentals.splice(alreadyHasToneIndex, 1);
                                }
                                if (solutionNote.a === 1) {
                                    barAccidentals.push({
                                        fullTone,
                                        a: 1,
                                    });
                                } else if (solutionNote.a === -1) {
                                    barAccidentals.push({
                                        fullTone,
                                        a: -1,
                                    });
                                } else {
                                    barAccidentals.push({
                                        fullTone,
                                        a: 0,
                                    });
                                }
                            }

                            return (
                                <Tone
                                    key={index}
                                    style={{
                                        height: (size.y / 2) * lowDim,
                                        top:
                                            (((-fullTone + 4 - clefOffset[clef]) * size.y) / 2 - size.y / 4) *
                                            lowDim,
                                    }}
                                >
                                    <ToneIn
                                        isLow={isLow}
                                        isChristmas={isChristmas}
                                        className={cn(
                                            solutionNote && solutionNote.p === fullTone && "Selected",
                                            incorrects &&
                                                incorrects[barIndex] &&
                                                incorrects[barIndex].indexOf(noteIndex) !== -1 &&
                                                solutionNote &&
                                                solutionNote.p === fullTone
                                                ? "Incorrect"
                                                : submited &&
                                                solutionNote &&
                                                solutionNote.p === fullTone &&
                                                "Correct",
                                            scale.findIndex(tones => tones.tone === fullTone) === -1 && "Passive",
                                            playingProps.note === noteIndex &&
                                            playingProps.bar === barIndex &&
                                            (checkPlaying || playing) && (!isToneset || (solutionNote && solutionNote.p === fullTone)) &&
                                            (checkPlaying ? "CheckingPlayNote" : "PlayingNote"),
                                            completed && "ToneDisabled",
                                            ledger && "Ledger",
                                            note.p === null && "Rest",
                                            hintable && fullTone === note.p && !solutionNote && "Fader",
                                            (!completed ||
                                                (completed && solutionNote && solutionNote.p === fullTone)) &&
                                            scale.findIndex(tones => tones.tone === fullTone) !== -1 &&
                                            note.tie &&
                                            "Tied",
                                            tiedOff && "TiedOff"
                                        )}
                                        isLastNoteOfBar={isLastNoteOfBar}
                                        color={
                                            incorrects &&
                                                incorrects[barIndex] &&
                                                incorrects[barIndex].indexOf(noteIndex) !== -1 &&
                                                solutionNote &&
                                                solutionNote.p === fullTone
                                                ? "Incorrect"
                                                : submited && solutionNote && solutionNote.p === fullTone
                                                    ? "Correct"
                                                    : solutionNote && solutionNote.p === fullTone
                                                        ? convertToNewColors(data.color)
                                                        : hintable && fullTone === note.p && !solutionNote
                                                            ? convertToNewColors(data.color)
                                                            : ""
                                        }
                                        style={{
                                            backgroundColor:
                                                solutionNote && solutionNote.p === fullTone
                                                    ? (isToneset ? "#ccc" : convertToNewColors(data.color))
                                                    : hintable && fullTone === note.p && !solutionNote
                                                        ? convertToNewColors(data.color)
                                                        : "",
                                            opacity:
                                                solutionNote &&
                                                    solutionNote.p !== fullTone &&
                                                    hintable &&
                                                    fullTone === note.p &&
                                                    solutionNote === null
                                                    ? 0.5
                                                    : 1,
                                        }}
                                        {...{
                                            [isMobile ? "onTouchStart" : "onClick"]: (e: TouchEvent | MouseEvent) =>
                                                clickTone(e, fullTone, solutionNote, barIndex, noteIndex),
                                        }}
                                    >
                                        {((solutionNote && solutionNote.p === fullTone && solutionNote.a === 1) ||
                                            (!solutionNote &&
                                                hintable &&
                                                note &&
                                                note.p === fullTone &&
                                                note.a === 1)) && 
                                                prevBarAccidentals.findIndex(a => a.fullTone === fullTone && a.a === 1) === -1 && (
                                                <Modifier scale={size.y * lowDim}>
                                                    {sf + accidental >= 7
                                                        ? <span className="s-double_sharp" />
                                                        : sf + accidental < 0
                                                            ? <span className="s-natural" />
                                                            : <span className="s-sharp" />
                                                    }
                                                </Modifier>
                                            )}
                                        {((solutionNote && solutionNote.p === fullTone && solutionNote.a === -1) ||
                                            (!solutionNote &&
                                                hintable &&
                                                note &&
                                                note.p === fullTone &&
                                                note.a === -1)) && 
                                                prevBarAccidentals.findIndex(a => a.fullTone === fullTone && a.a === -1) === -1 && (
                                                <Modifier scale={size.y * lowDim} isDoubleFlat={sf + accidental < 0}>
                                                    {sf + accidental >= 7
                                                        ? <span className="s-natural" />
                                                        : sf + accidental >= 0
                                                            ? <span className="s-flat" />
                                                            : <span className="s-double_flat" />
                                                    }
                                                </Modifier>
                                            )}
                                        {solutionNote &&
                                            solutionNote.p === fullTone &&
                                            solutionNote.a === 0 &&
                                            prevBarAccidentals.findIndex(a => a.fullTone === fullTone && a.a !== 0) !== -1 && (
                                                <Modifier scale={size.y * lowDim}>
                                                    {sf + accidental >= 7
                                                        ? <span className="s-sharp" />
                                                        : sf + accidental < 0
                                                            ? <span className="s-flat" />
                                                            : <span className="s-natural" />
                                                    }
                                                </Modifier>
                                            )}
                                    </ToneIn>
                                </Tone>
                            );
                        })}
                    </Note>
                );
            })}
            {truncBarline && !barlineType && barIndex >= bars.length - 1 && (
                <>
                    {truncBarline.type === "double" && <DoubleBarline width={size.y * lowDim} />}
                    {truncBarline.type === "final" && <FinalBarline width={size.y * lowDim} />}
                </>
            )}
            {barIndex < bars.length - 1 &&
                (barlines.findIndex(barline => barline.bar === barIndex) === -1
                    ? (
                        <Barline width={size.y * lowDim} isChristmas={isChristmas} />
                    ) : barlineType.indexOf("double") !== -1 ? (
                        <>
                            {(barlineType.indexOf("RepeatEnd") !== -1 || barlineType.indexOf("RepeatBoth") !== -1) && (
                                <DotsBefore
                                    style={{
                                        marginTop: size.y * 2 * lowDim,
                                    }}
                                    size={size}
                                    playing={playing || checkPlaying}
                                    isChristmas={isChristmas}
                                />
                            )}
                            <DoubleBarline width={size.y * lowDim} isChristmas={isChristmas} />
                            {(barlineType.indexOf("RepeatStart") !== -1 ||
                                barlineType.indexOf("RepeatBoth") !== -1) && (
                                    <DotsBefore
                                        style={{
                                            marginTop: size.y * 2 * lowDim,
                                        }}
                                        size={size}
                                        playing={playing || checkPlaying}
                                        isChristmas={isChristmas}
                                    />
                                )}
                        </>
                    )
                        : barlineType.indexOf("final") !== -1
                            ? (
                                <>
                                    {(barlineType.indexOf("RepeatEnd") !== -1 || barlineType.indexOf("RepeatBoth") !== -1) && (
                                        <DotsBefore
                                            style={{
                                                marginTop: size.y * 2 * lowDim,
                                            }}
                                            size={size}
                                            playing={playing || checkPlaying}
                                            isChristmas={isChristmas}
                                        />
                                    )}
                                    {(barlineType === "finalRepeatEnd" ||
                                        barlineType === "final") && <FinalBarline width={size.y * lowDim} />}
                                    {barlineType === "finalRepeatStart" && <FinalStartBarline width={size.y * lowDim} />}
                                    {barlineType === "finalRepeatBoth" && <FinalBothBarline width={size.y * lowDim} />}
                                    {(barlineType.indexOf("RepeatStart") !== -1 ||
                                        barlineType.indexOf("RepeatBoth") !== -1) && (
                                            <DotsBefore
                                                style={{
                                                    marginTop: size.y * 2 * lowDim,
                                                }}
                                                size={size}
                                                playing={playing || checkPlaying}
                                                isChristmas={isChristmas}
                                            />
                                        )}
                                </>
                            )
                            : (
                                <Barline width={size.y * lowDim} isChristmas={isChristmas} />
                            ))}
        </StyledBar>
    );
}

export default memo(Bar, (props: IProps, nextProps: IProps) => {
    if (
        props.playingProps.bar === nextProps.playingProps.bar &&
        props.playingProps.bar === props.barIndex
    ) {
        return false;
    }
    if (
        props.playingProps.bar && props.playingProps.bar + 1 === nextProps.playingProps.bar &&
        nextProps.playingProps.note === 0 &&
        (props.playingProps.bar === props.barIndex || nextProps.playingProps.bar === props.barIndex)
    ) {
        return false;
    }
    if (
        (nextProps.playingProps.bar === 1 || nextProps.playingProps.bar === 0) &&
        nextProps.playingProps.note === 0 &&
        props.barIndex === nextProps.playingProps.bar
    ) {
        return false;
    }
    if (nextProps.size.x !== props.size.x || nextProps.size.y !== props.size.y) {
        return false;
    }
    if (nextProps.data.hint !== props.data.hint) {
        return false;
    }
    if (nextProps.data.color !== props.data.color) {
        return false;
    }
    if (JSON.stringify(nextProps.incorrects) !== JSON.stringify(props.incorrects)) {
        return false;
    }
    if (nextProps.completed !== props.completed) {
        return false;
    }

    for (const s in props.solutions[props.barIndex]) {
        if (props.solutions[props.barIndex][s] !== nextProps.solutions[props.barIndex][s]) {
            return false;
        }
    }
    if (props.playing && !nextProps.playing) {
        return false;
    }
    if (props.checkPlaying && !nextProps.checkPlaying) {
        return false;
    }
    if (props.playing !== !nextProps.playing) {
        return false;
    }
    if (props.checkPlaying !== !nextProps.checkPlaying) {
        return false;
    }
    return true;
});
